import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import { signOut } from "firebase/auth";
import "./EventDashboard.css";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const [hours, minutes] = timeStr.split(":").map(Number);
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const isFirestoreTimestampObject = (obj) =>
  obj && typeof obj === "object" && "seconds" in obj && "nanoseconds" in obj;

const toDateObj = (dateVal) => {
  if (!dateVal) return null;
  if (typeof dateVal.toDate === "function") return dateVal.toDate();
  if (dateVal instanceof Date) return dateVal;
  if (isFirestoreTimestampObject(dateVal))
    return new Date(dateVal.seconds * 1000);
  if (typeof dateVal === "string") return new Date(dateVal);
  return null;
};

const EventDashboard = () => {
  const [events, setEvents] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [adding, setAdding] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: "",
    description: "",
    date: "",
    startTime: "",
    endTime: "",
    address: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEvents = async () => {
      const querySnapshot = await getDocs(collection(db, "events"));
      const data = querySnapshot.docs.map((docSnap) => {
        const event = docSnap.data();

        // Normalize Firestore timestamp
        let parsedDate = "";

        // Check if Firestore Timestamp
        if (event.date && typeof event.date.toDate === "function") {
          parsedDate = event.date.toDate().toISOString().slice(0, 10); // e.g., '2025-06-30'
        } else if (
          event.date &&
          typeof event.date === "object" &&
          "seconds" in event.date &&
          "nanoseconds" in event.date
        ) {
          // Timestamp object
          parsedDate = new Date(event.date.seconds * 1000)
            .toISOString()
            .slice(0, 10);
        } else if (typeof event.date === "string") {
          parsedDate = event.date;
        } else {
          parsedDate = ""; // Fallback
        }

        return {
          id: docSnap.id,
          ...event,
          date: parsedDate,
        };
      });

      setEvents(data);
    };

    fetchEvents();
  }, []);

  const handleEditClick = (event) => {
    setEditId(event.id);
    setEditForm({
      ...event,
      date: toDateObj(event.date),
    });
  };

  const handleCancelClick = () => {
    setEditId(null);
    setEditForm({});
  };

  const handleChange = (e, field) => {
    setEditForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveClick = async (id) => {
    try {
      const eventRef = doc(db, "events", id);
      const updatedData = {
        ...editForm,
        date: Timestamp.fromDate(new Date(editForm.date)),
      };

      await updateDoc(eventRef, updatedData);
      setEvents((prev) =>
        prev.map((ev) => (ev.id === id ? { ...updatedData, id } : ev))
      );
      setEditId(null);
      setEditForm({});
    } catch (error) {
      console.error("Error updating event:", error);
    }
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    try {
      await deleteDoc(doc(db, "events", id));
      setEvents((prev) => prev.filter((ev) => ev.id !== id));
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  const handleNewChange = (e, field) => {
    setNewEvent((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleAddNew = async () => {
    try {
      const eventData = {
        ...newEvent,
        date: Timestamp.fromDate(new Date(newEvent.date)),
      };

      const docRef = await addDoc(collection(db, "events"), eventData);
      setEvents((prev) => [...prev, { id: docRef.id, ...eventData }]);

      setNewEvent({
        title: "",
        description: "",
        date: "",
        startTime: "",
        endTime: "",
        address: "",
      });
      setAdding(false);
    } catch (error) {
      console.error("Error adding event:", error);
    }
  };

  const handleLogout = () => {
    signOut(auth);
    navigate("/adminLogin");
  };

  return (
    <div>
      <Header />
      <div className="admin-container">
        <div className="event-dashboard">
          <h2>Event Management</h2>

          <button onClick={() => setAdding((prev) => !prev)}>
            {adding ? "Cancel Add" : "Add New Event"}
          </button>

          {adding && (
            <div className="event-form">
              {/* Title */}
              <div className="form-field">
                <label>Title</label>
                <input
                  value={newEvent.title}
                  onChange={(e) => handleNewChange(e, "title")}
                  placeholder="Title"
                />
              </div>

              {/* Date */}
              <div className="form-field">
                <label>Date</label>
                <input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => handleNewChange(e, "date")}
                />
              </div>

              {/* Start Time */}
              <div className="form-field">
                <label>Start Time</label>
                <input
                  type="time"
                  value={newEvent.startTime}
                  onChange={(e) => handleNewChange(e, "startTime")}
                />
              </div>

              {/* End Time */}
              <div className="form-field">
                <label>End Time</label>
                <input
                  type="time"
                  value={newEvent.endTime}
                  onChange={(e) => handleNewChange(e, "endTime")}
                />
              </div>

              {/* Address */}
              <div className="form-field full-width">
                <label>Address</label>
                <input
                  value={newEvent.address}
                  onChange={(e) => handleNewChange(e, "address")}
                  placeholder="Address"
                />
              </div>

              {/* Description */}
              <div className="form-field full-width">
                <label>Description</label>
                <textarea
                  rows={4}
                  value={newEvent.description}
                  onChange={(e) => handleNewChange(e, "description")}
                  placeholder="Description"
                />
              </div>

              <button onClick={handleAddNew}>Save New Event</button>
            </div>
          )}

          <table className="event-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Date</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Address</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id}>
                  <td>
                    {editId === ev.id ? (
                      <input
                        value={editForm.title}
                        onChange={(e) => handleChange(e, "title")}
                      />
                    ) : (
                      ev.title
                    )}
                  </td>
                  <td>
                    {editId === ev.id ? (
                      <DatePicker
                        selected={
                          editForm.date instanceof Date
                            ? editForm.date
                            : new Date(editForm.date)
                        }
                        onChange={(date) =>
                          setEditForm((prev) => ({ ...prev, date }))
                        }
                        dateFormat="yyyy-MM-dd"
                      />
                    ) : typeof ev.date === "string" ? (
                      ev.date
                    ) : (
                      ev.date?.toDate?.().toISOString().slice(0, 10) || ""
                    )}
                  </td>
                  <td>
                    {editId === ev.id ? (
                      <input
                        type="time"
                        value={editForm.startTime}
                        onChange={(e) => handleChange(e, "startTime")}
                      />
                    ) : (
                      formatTime(ev.startTime)
                    )}
                  </td>
                  <td>
                    {editId === ev.id ? (
                      <input
                        type="time"
                        value={editForm.endTime}
                        onChange={(e) => handleChange(e, "endTime")}
                      />
                    ) : (
                      formatTime(ev.endTime)
                    )}
                  </td>
                  <td>
                    {editId === ev.id ? (
                      <input
                        value={editForm.address}
                        onChange={(e) => handleChange(e, "address")}
                      />
                    ) : (
                      ev.address
                    )}
                  </td>
                  <td>
                    {editId === ev.id ? (
                      <textarea
                        rows={4}
                        value={editForm.description}
                        onChange={(e) => handleChange(e, "description")}
                      />
                    ) : ev.description && ev.description.length > 60 ? (
                      ev.description.slice(0, 60) + "..."
                    ) : (
                      ev.description
                    )}
                  </td>
                  <td>
                    {editId === ev.id ? (
                      <>
                        <button onClick={() => handleSaveClick(ev.id)}>
                          Save
                        </button>
                        <button onClick={handleCancelClick}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => handleEditClick(ev)}>
                          Edit
                        </button>
                        <button onClick={() => handleDeleteClick(ev.id)}>
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <button className="btn" onClick={handleLogout}>Logout</button>
    </div>
  );
};

export default EventDashboard;
